import { toInteger } from 'lodash'
import * as React from 'react'
import { Route, Routes, useLocation, useParams, useSearchParams } from 'react-router-dom'

import { BottomLoadingIndicator } from '@paytrace/pt-skel/Component'
import { ConversationIcon } from '@paytrace/pt-skel/Component/Icon'
import { SSO } from '@paytrace/pt-skel/Page/SSO'

import Dev from './Dev'
import DynamicPage from './DynamicPage'
import { ActionType } from './Helper/ActionType'
import { MANAGE_ENHANCED_DATA_PAGE_ROUTE } from './Helper/EnhancedData/Constants'
import NotFound from './NotFound'

import CaptureTransactions from './Page/CaptureTransactions'
import Dashboard from './Page/Dashboard'
import DeleteStoreAndForward from './Page/DeleteStoreAndForward'
import Level2 from './Page/Level2'
import Level3 from './Page/Level3'
import Login from './Page/Login'
import MastercardL3Default from './Page/MastercardL3Default'
import MultipleTransactions from './Page/MultipleTransactions'
import RefundTransactions from './Page/RefundTransactions'
import SettleTransactions from './Page/SettleTransactions'
// import StoreAndForward from './Page/StoreAndForward'
import SemiIntegratedCard from './Page/SemiIntegratedCard'
import SwipeCard from './Page/SwipeCard'
import ViewBatchReports from './Page/ViewBatchReports'
import ViewTransactions from './Page/ViewTransactions'
import ViewTransactionsResultsOnly from './Page/ViewTransactionsResultsOnly'
import VirtualTerminal from './Page/VirtualTerminal'
import VisaL3Default from './Page/VisaL3Default'
import VoidTransactions from './Page/VoidTransactions'
import { EnhancedDataSettingsSection, ManageEnhancedData, ViewEnhancedDataTransactions } from './Page/EnhancedData'
import RecurCreate from './Page/Recurring/RecurCreate'
import RecurEdit from './Page/Recurring/RecurEdit'
import ViewPayments from './Page/Recurring/ViewPayments'

function NotFoundSlug () {
  const { url } = useParams()

  return (
    <DynamicPage section=''>
      <NotFound url={url as string} />
    </DynamicPage>
  )
}

function KeyEnterPage () {
  const [searchParams, _] = useSearchParams()

  return (
    <DynamicPage section='vt' showBottomLoading={false}>
      <VirtualTerminal
        action_type={ActionType.KEYED}
        transactionId={searchParams.get('tranxid')}
        customerId={searchParams.get('cid')}
      />
    </DynamicPage>
  )
}

function SwipeCardPage () {
  const [searchParams, _] = useSearchParams()
  const data = useLocation().state.data

  return (
    <DynamicPage section='vt'>
      <VirtualTerminal
        action_type={ActionType.SWIPE}
        transactionId={searchParams.get('tranxid')}
        customerId={searchParams.get('cid')}
        card_info={data?.card_info || null}
        swipe={data?.swipe}
      />
    </DynamicPage>
  )
}

function EnhancedDataPage ({ children }: React.PropsWithChildren<{}>) {
  return (
    <DynamicPage section='Enhanced Data' footerIcon={<ConversationIcon />}>
      {children}
    </DynamicPage>
  )
}

function ManageEnhancedDataPage () {
  const [searchParams, _] = useSearchParams()
  const state = useLocation().state
  const transactionId: number = toInteger(searchParams.get('tranxid'))
  const cid: number = toInteger(searchParams.get('cid'))
  return (
    <ManageEnhancedData
      transactionId={transactionId}
      cardLevel={state?.cardLevel}
      customerId={cid}
    />
  )
}

export const RouteConfig = (
  <Routes>

    <Route path='/' element={
      <DynamicPage section='dashboard'>
        <Dashboard />
      </DynamicPage>
    } />

    <Route path='/dashboard'
      element={
        <DynamicPage section='dashboard'>
          <Dashboard />
        </DynamicPage>
      } />

    <Route path='/tranx.pay'
      element={<KeyEnterPage />}
    />

    <Route path='/swipe.pay' element={
      <DynamicPage section='vt'>
        <SwipeCard />
      </DynamicPage>
    } />

    <Route path='/enhanceddatasettings.pay' element={
      <EnhancedDataPage>
        <EnhancedDataSettingsSection />
      </EnhancedDataPage>
    } />

    <Route path='/enhanceddatatransactions.pay' element={
      <EnhancedDataPage>
        <ViewEnhancedDataTransactions />
      </EnhancedDataPage>
    } />

    <Route path={MANAGE_ENHANCED_DATA_PAGE_ROUTE} element={
      <EnhancedDataPage>
        <ManageEnhancedDataPage/>
      </EnhancedDataPage>
    } />

    <Route path='/semiintegrated.pay' element={
      <DynamicPage section='vt'>
        <SemiIntegratedCard />
      </DynamicPage>
    } />

    <Route path='/swipetranx.pay'
           element={< SwipeCardPage />}
    />

    <Route path='/multipletranx.pay' element={
      <DynamicPage section='vt'>
        <MultipleTransactions />
      </DynamicPage>
    } />

    <Route path='/storeforward.pay' element={
      <DynamicPage section='vt'>
        {/* <StoreAndForward /> */}
        <NotFound url='storeforward.pay' />
      </DynamicPage>
    } />

    <Route path='/deletestoreforward.pay' element={
      <DynamicPage section='vt'>
        <DeleteStoreAndForward />
      </DynamicPage>
    } />

    <Route path='/void.pay' element={
      <DynamicPage section='vt'>
        <VoidTransactions />
      </DynamicPage>
    } />

    <Route path='/refund.pay' element={
      <DynamicPage section='vt'>
        <RefundTransactions />
      </DynamicPage>
    } />

    <Route path='/capture.pay' element={
      <DynamicPage section='vt'>
        <CaptureTransactions />
      </DynamicPage>
    } />

    <Route path='/level3.pay' element={
      <DynamicPage section='vt'>
        <Level3 />
      </DynamicPage>
    } />

    <Route path='/VisaL3Default.pay' element={
      <DynamicPage section='vt'>
        <VisaL3Default />
      </DynamicPage>
    } />

    <Route path='/MCRDL3Default.pay' element={
      <DynamicPage section='vt'>
        <MastercardL3Default />
      </DynamicPage>
    } />

    <Route path='/level2.pay' element={
      <DynamicPage section='vt'>
        <Level2 />
      </DynamicPage>
    } />

    <Route path='/searchbatches.pay' element={
      <DynamicPage section='vt'>
        <ViewBatchReports />
      </DynamicPage>
    } />

    <Route path='/mu/transactions-only' element={
      <ViewTransactionsResultsOnly />
    } />

    <Route path='/mu/transactions' element={
      <DynamicPage section='vt'>
        <ViewTransactions />
      </DynamicPage>
    } />

    <Route path='/settle.pay' element={
      <DynamicPage section='vt'>
        <SettleTransactions />
      </DynamicPage>
    } />

    <Route path='/sso.pay'
      element={
        <React.Fragment>
          <SSO useModalFallback={true} />
          <BottomLoadingIndicator />
        </React.Fragment>
      } />

    <Route path='/login.pay'
      element={
        <React.Fragment>
          <Login />
          <BottomLoadingIndicator />
        </React.Fragment>
      } />

    <Route path='/payments'
      element={
        <DynamicPage section='payments'>
          <React.Fragment />
        </DynamicPage>
      } />

    <Route path='/createrecur.pay'
      element={
        <DynamicPage section='recur'>
          <RecurCreate />
        </DynamicPage>
      } />

    <Route path='/modifyrecur.pay'
      element={
        <DynamicPage section='recur'>
          <RecurEdit />
        </DynamicPage>
      } />

    <Route path='/viewrecur.pay'
      element={
        <DynamicPage section='recur'>
          <ViewPayments />
        </DynamicPage>
      } />

    <Route path='/clientele'
      element={<DynamicPage section='clientele' />} />

    <Route path='/admin'
      element={<DynamicPage section='admin' />} />

    <Route path='/dev'
      element={
        <DynamicPage section='vt'>
          <Dev widget='' />
        </DynamicPage>
      } />

    <Route
      path='/:url'
      element={<NotFoundSlug />} />

    <Route path='/*' element={<NotFound url='?' />} />

  </Routes>
)

export default RouteConfig
